
'use client';

import Link from 'next/link';
import { Logo } from '@/components/ui/logo';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Code, Smartphone, Server, BrainCircuit, Wifi, Scan, Orbit, CheckCircle, Menu, Info, Users, Briefcase, Handshake, Newspaper, BookText, FileText, Quote, HelpCircle, PenSquare, LifeBuoy, Wrench, BarChart, Route, FolderGit2 } from 'lucide-react';
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"

const services = [
  {
    title: "Web Development",
    href: "/services/software-development",
    description: "Modern web solutions using React, Next.js, and more.",
    icon: Code
  },
  {
    title: "Mobile App Development",
    href: "/services/mobile-app-development",
    description: "Cross-platform and native apps for iOS and Android.",
    icon: Smartphone
  },
  {
    title: "Cloud Services",
    href: "/services/cloud-services",
    description: "Scalable, secure, and resilient cloud infrastructure.",
    icon: Server
  },
  {
    title: "AI & Machine Learning",
    href: "/services/ai-machine-learning",
    description: "Intelligent solutions leveraging AI, ML, and GenAI.",
    icon: BrainCircuit
  },
  {
    title: "IoT & IIoT Solutions",
    href: "/services/iot-solutions",
    description: "Connecting and controlling devices in real-time.",
    icon: Wifi
  },
  {
    title: "Computer Vision",
    href: "/services/computer-vision",
    description: "Image processing for quality management and analysis.",
    icon: Scan
  },
  {
    title: "Electronics & Hardware",
    href: "/services/electronics-hardware",
    description: "Custom electronic devices and embedded systems.",
    icon: Orbit
  },
  {
    title: "Quality Management Systems",
    href: "/services/quality-management-systems",
    description: "Custom software to ensure the highest quality standards.",
    icon: CheckCircle
  }
];

const companyLinks = [
    { href: "/about", label: 'About Us', description: 'Learn about our mission, vision, and values.', icon: Info },
    { href: "/team", label: 'Our Team', description: 'Meet the talented individuals behind our success.', icon: Users },
    { href: "/careers", label: 'Careers', description: 'Explore exciting opportunities to join our team.', icon: Briefcase },
    { href: "/partners", label: 'Partners', description: 'Discover our network of trusted collaborators.', icon: Handshake },
];

const resourceLinks = [
    { href: "/blogs", label: 'Blogs', description: 'Insights and articles on the latest in technology.', icon: BookText },
    { href: "/projects", label: 'Projects', description: 'A showcase of our past work and successes.', icon: FolderGit2 },
    { href: "/faq", label: 'FAQ', description: 'Find answers to frequently asked questions.', icon: HelpCircle },
    { href: "/iot-demos", label: 'IoT Demos', description: 'Interactive demos of our IoT solutions.', icon: BarChart },
    { href: "/support", label: 'Support', description: 'Get help and support from our team.', icon: LifeBuoy },
];

function DesktopNav() {
    const [servicesOpen, setServicesOpen] = useState(false);
    const [companyOpen, setCompanyOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);

    return (
        <nav className="ml-auto hidden md:flex items-center gap-4 sm:gap-6">
          <Link href="/ai-tools" className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              AI Tools
          </Link>
          <Popover open={servicesOpen} onOpenChange={setServicesOpen}>
            <PopoverTrigger
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Services <ChevronDown className="ml-1 h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent className="w-screen max-w-4xl" align="center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                {services.map((service) => (
                   <Link
                    key={service.title}
                    href={service.href}
                    className="flex items-start space-x-4 rounded-lg p-3 hover:bg-muted"
                    onClick={() => setServicesOpen(false)}
                  >
                    <service.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">{service.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {service.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="bg-muted px-6 py-4 text-center">
                  <p className="text-sm">
                    Looking for a custom solution?{' '}
                    <Link href="/services" onClick={() => setServicesOpen(false)} className="text-primary font-semibold hover:underline">
                      View all services
                    </Link>{' '}
                    or{' '}
                    <Link href="/#contact" onClick={() => setServicesOpen(false)} className="text-primary font-semibold hover:underline">
                      contact us
                    </Link>{' '}
                    to discuss your project.
                  </p>
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
            <PopoverTrigger 
                onClick={() => setCompanyOpen(!companyOpen)}
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Company <ChevronDown className="ml-1 h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent className="w-screen max-w-lg" align="center">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                    {companyLinks.map(link => (
                         <Link
                          key={link.href}
                          href={link.href}
                          className="flex items-start space-x-4 rounded-lg p-3 hover:bg-muted"
                          onClick={() => setCompanyOpen(false)}
                        >
                          <link.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                          <div className="space-y-1">
                            <p className="font-semibold text-foreground">{link.label}</p>
                            <p className="text-sm text-muted-foreground">
                              {link.description}
                            </p>
                          </div>
                        </Link>
                    ))}
                </div>
            </PopoverContent>
          </Popover>
           <Popover open={resourcesOpen} onOpenChange={setResourcesOpen}>
            <PopoverTrigger 
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Resources <ChevronDown className="ml-1 h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent className="w-screen max-w-lg" align="center">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                     {resourceLinks.map(link => (
                          <Link
                          key={link.href}
                          href={link.href}
                          className="flex items-start space-x-4 rounded-lg p-3 hover:bg-muted"
                          onClick={() => setResourcesOpen(false)}
                        >
                          <link.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                          <div className="space-y-1">
                            <p className="font-semibold text-foreground">{link.label}</p>
                            <p className="text-sm text-muted-foreground">
                              {link.description}
                            </p>
                          </div>
                        </Link>
                    ))}
                </div>
            </PopoverContent>
          </Popover>
        </nav>
    )
}

function MobileNav() {
    return (
         <div className="md:hidden ml-auto">
            <Sheet>
                <SheetTrigger asChild>
                    <Button variant="ghost" size="icon">
                        <Menu className="h-6 w-6" />
                        <span className="sr-only">Toggle Menu</span>
                    </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-sm">
                    <div className="p-4">
                        <Logo />
                        <div className="mt-8 flex flex-col gap-1">
                             <SheetClose asChild>
                                <Link href="/ai-tools" className="flex items-center py-4 font-semibold text-left w-full border-b">AI Tools</Link>
                             </SheetClose>
                             <Accordion type="single" collapsible className="w-full">
                                <AccordionItem value="services">
                                    <AccordionTrigger className="font-semibold">Services</AccordionTrigger>
                                    <AccordionContent>
                                        <div className="flex flex-col gap-2 pl-4">
                                            {services.map(service => (
                                                <SheetClose asChild key={service.href}>
                                                    <Link href={service.href} className="text-muted-foreground hover:text-foreground">{service.title}</Link>
                                                </SheetClose>
                                            ))}
                                             <SheetClose asChild>
                                                <Link href="/services" className="text-primary font-semibold mt-2">View All Services</Link>
                                             </SheetClose>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                                 <AccordionItem value="company">
                                    <AccordionTrigger className="font-semibold">Company</AccordionTrigger>
                                    <AccordionContent>
                                        <div className="flex flex-col gap-2 pl-4">
                                            {companyLinks.map(link => (
                                                 <SheetClose asChild key={link.href}>
                                                    <Link href={link.href} className="text-muted-foreground hover:text-foreground">{link.label}</Link>
                                                 </SheetClose>
                                            ))}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                                 <AccordionItem value="resources">
                                    <AccordionTrigger className="font-semibold">Resources</AccordionTrigger>
                                    <AccordionContent>
                                        <div className="flex flex-col gap-2 pl-4">
                                            {resourceLinks.map(link => (
                                                <SheetClose asChild key={link.href}>
                                                    <Link href={link.href} className="text-muted-foreground hover:text-foreground">{link.label}</Link>
                                                </SheetClose>
                                            ))}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                              <SheetClose asChild>
                                <Button asChild className="mt-6 bg-gradient-to-r from-vibrant-purple to-electric-blue text-white">
                                    <Link href="/#contact">Get a Quote</Link>
                                </Button>
                             </SheetClose>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
         </div>
    )
}

export function Header() {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 max-w-7xl items-center">
        <Logo />
        <DesktopNav />
        <div className="ml-4 hidden md:block">
          <Button asChild className="bg-gradient-to-r from-vibrant-purple to-electric-blue text-white">
            <Link href="/#contact">Get a Quote</Link>
          </Button>
        </div>
        <MobileNav />
      </div>
    </header>
  );
}
